import { Controller } from '@hotwired/stimulus'
import { parseCSV, toCSV, downloadCSV } from '../lib/smart_csv_import/csv_parser'
import { autoMapColumns, applyMapping } from '../lib/smart_csv_import/column_mapper'

const STATUS = {
  IDLE: 'idle',
  READY: 'ready',
  EDITING: 'editing',
  ERROR: 'error'
}

export default class extends Controller {
  static targets = [
    'dropzone', 'fileInput', 'fileName', 'mappingStatus', 'stats', 'tableBody',
    'tableHead', 'pagination', 'errorBanner', 'panel', 'launchButton', 'revalidateButton'
  ]

  static values = {
    schema: Object,
    messages: { type: Object, default: {} },
    pageSize: { type: Number, default: 25 },
    outputFilename: { type: String, default: 'import.csv' },
    validDownloadFilename: { type: String, default: 'import_valid.csv' },
    errorsDownloadFilename: { type: String, default: 'import_errors.csv' },
    requireSubmitButton: { type: Boolean, default: true },
    requireLaunchEdition: { type: Boolean, default: true },
    interceptSubmit: { type: Boolean, default: true },
    submitWithFetch: { type: Boolean, default: true }
  }

  connect () {
    this.columnNames = this.schemaValue.columns.map((column) => column.name)
    this.status = STATUS.IDLE
    this.currentPage = 0
    this.transformedData = []
    this.validRows = []
    this.errorRows = []
    this.rowErrors = new Map()
    this.parseErrors = []
    this.mapping = {}
    this.uploadedFile = null
    this.form = this.element.closest('form')

    if (this.form && this.interceptSubmitValue) {
      this.boundPrepareSubmit = this.prepareSubmit.bind(this)
      this.form.addEventListener('submit', this.boundPrepareSubmit)
    }
  }

  disconnect () {
    if (this.form && this.boundPrepareSubmit) {
      this.form.removeEventListener('submit', this.boundPrepareSubmit)
    }
  }

  dragOver (event) {
    event.preventDefault()
    this.dropzoneTarget.classList.add('border-blue-500', 'bg-blue-50')
  }

  dragLeave () {
    this.dropzoneTarget.classList.remove('border-blue-500', 'bg-blue-50')
  }

  async drop (event) {
    event.preventDefault()
    this.dragLeave()
    const file = event.dataTransfer?.files?.[0]
    if (file) await this.processFile(file)
  }

  async fileSelected (event) {
    const file = event.target.files?.[0]
    if (file) await this.processFile(file)
  }

  async processFile (file) {
    if (!file.name.toLowerCase().endsWith('.csv')) {
      this.showError(this.message('invalidFileType'))
      return
    }

    this.clearImportState()
    this.uploadedFile = file
    this.hideError()
    this.setFileName(file.name)

    try {
      const result = await parseCSV(file)
      if (result.data.length === 0) {
        this.discardUploadedFile()
        this.showError(this.message('emptyFile'))
        return
      }

      this.parseErrors = result.parseErrors || []

      const mappingResult = autoMapColumns(result.columns, this.schemaValue)
      if (mappingResult.status === 'none') {
        this.showWarning(this.message('mappingFailed'))
      }

      this.mapping = mappingResult.mapping
      this.renderMappingStatus(mappingResult)
      this.applyCurrentMapping(result.data)
      this.panelTarget.classList.remove('hidden')

      if (this.parseErrors.length > 0) {
        this.showWarning(this.message('parsePartial'))
      }

      this.enterEditionMode()
    } catch {
      this.discardUploadedFile()
      this.showError(this.message('parseFailed'))
      this.status = STATUS.ERROR
    }
  }

  launchEdition () {
    if (this.transformedData.length === 0) {
      this.showError(this.message('noRowsToEdit'))
      return
    }

    this.enterEditionMode()
  }

  enterEditionMode () {
    if (this.transformedData.length === 0) return

    if (this.errorRows.length > 0) {
      this.showWarning(this.rowsNeedReviewMessage())
    } else {
      this.hideError()
    }

    this.status = STATUS.EDITING
    this.toggleLaunchButton(false)
    this.toggleRevalidateButton(this.errorRows.length > 0)
    this.renderTable()
  }

  revalidate () {
    if (!this.uploadedFile) return
    this.applyCurrentMapping(this.transformedData)
    this.renderTable()
  }

  updateCell (event) {
    if (this.status !== STATUS.EDITING) return

    const rowIndex = Number(event.target.dataset.rowIndex)
    const column = event.target.dataset.column
    const actualIndex = this.currentPage * this.pageSizeValue + rowIndex

    this.transformedData[actualIndex] = {
      ...this.transformedData[actualIndex],
      [column]: event.target.value
    }

    this.applyCurrentMapping(this.transformedData)
    this.renderTable()
  }

  previousPage () {
    if (this.currentPage > 0) {
      this.currentPage -= 1
      this.renderTable()
    }
  }

  nextPage () {
    const totalPages = Math.ceil(this.transformedData.length / this.pageSizeValue)
    if (this.currentPage < totalPages - 1) {
      this.currentPage += 1
      this.renderTable()
    }
  }

  downloadValid () {
    downloadCSV(this.validRows, this.validDownloadFilenameValue, this.columnNames)
  }

  downloadErrors () {
    downloadCSV(this.errorRows, this.errorsDownloadFilenameValue, this.columnNames)
  }

  reset () {
    this.clearImportState()
    this.discardUploadedFile()
    this.setFileName('')
    this.hideError()
  }

  clearImportState () {
    this.status = STATUS.IDLE
    this.currentPage = 0
    this.transformedData = []
    this.validRows = []
    this.errorRows = []
    this.rowErrors = new Map()
    this.parseErrors = []
    this.mapping = {}
    this.panelTarget.classList.add('hidden')
    this.toggleLaunchButton(false)
    this.toggleRevalidateButton(false)
  }

  discardUploadedFile () {
    this.uploadedFile = null
    if (this.hasFileInputTarget) this.fileInputTarget.value = ''
  }

  prepareSubmit (event) {
    if (!this.shouldValidateSubmit(event)) return

    if (!this.uploadedFile || this.status === STATUS.IDLE) {
      if (this.hasFileInputTarget && this.fileInputTarget.files.length === 0) {
        event.preventDefault()
        this.showError(this.message('missingFile'))
      }
      return
    }

    if (this.status === STATUS.READY && this.requireLaunchEditionValue) {
      event.preventDefault()
      this.showError(this.message('launchEditionRequired'))
      return
    }

    if (this.status === STATUS.ERROR) {
      event.preventDefault()
      this.showError(this.message('mappingErrors'))
      return
    }

    if (this.validRows.length === 0) {
      event.preventDefault()
      this.showError(this.message('noValidRowsSubmit'))
      return
    }

    if (!this.submitWithFetchValue) {
      this.injectValidRowsFile()
      return
    }

    event.preventDefault()
    this.submitWithValidRows()
  }

  submitWithValidRows () {
    const formData = new FormData(this.form)

    formData.set(this.fileInputTarget.name, this.buildOutputFile())

    fetch(this.form.action, {
      method: this.form.method || 'POST',
      body: formData,
      credentials: 'same-origin',
      headers: {
        Accept: 'text/html',
        'X-CSRF-Token': this.csrfToken()
      }
    }).then((response) => {
      if (response.redirected) {
        window.location.href = response.url
        return
      }

      return response.text().then((html) => {
        document.open()
        document.write(html)
        document.close()
      })
    }).catch(() => {
      this.showError(this.message('submitFailed'))
    })
  }

  injectValidRowsFile () {
    const dataTransfer = new DataTransfer()

    dataTransfer.items.add(this.buildOutputFile())
    this.fileInputTarget.files = dataTransfer.files
  }

  buildOutputFile () {
    const csv = toCSV(this.validRows, this.columnNames)

    return new File([csv], this.outputFilenameValue, { type: 'text/csv' })
  }

  csrfToken () {
    return document.querySelector('meta[name="csrf-token"]')?.content || ''
  }

  shouldValidateSubmit (event) {
    if (!this.requireSubmitButtonValue) return true

    return event.submitter?.type === 'submit'
  }

  message (key, replacements = {}) {
    const snakeKey = key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`)
    let text = this.messagesValue[snakeKey] || this.messagesValue[key] || ''

    Object.entries(replacements).forEach(([name, value]) => {
      text = text.replace(`%{${name}}`, String(value))
    })

    return text
  }

  rowsNeedReviewMessage () {
    return this.message('rowsNeedReview', { count: this.errorRows.length })
  }

  applyCurrentMapping (data) {
    const result = applyMapping(data, this.mapping, this.schemaValue)

    this.transformedData = result.data
    this.validRows = result.validRows
    this.errorRows = result.errorRows
    this.rowErrors = result.errors
    this.mergeParseErrorsIntoRowErrors()
    this.renderStats(result.stats)

    if (this.status === STATUS.EDITING) this.renderTable()
  }

  mergeParseErrorsIntoRowErrors () {
    this.parseErrors.forEach((parseError) => {
      const rowIndex = parseError.row
      if (rowIndex === undefined || rowIndex === null) return

      const existing = this.rowErrors.get(rowIndex) || []
      const parseRowError = { column: null, message: parseError.message, type: 'parse' }

      if (!existing.some((error) => error.type === 'parse' && error.message === parseError.message)) {
        existing.push(parseRowError)
      }

      this.rowErrors.set(rowIndex, existing)

      const row = this.transformedData.find((item) => item.__rowIndex === rowIndex)
      if (row) row.__hasError = true
    })
  }

  renderMappingStatus (mappingResult) {
    this.mappingStatusTarget.textContent = this.message(`mapping_${mappingResult.status}`, {
      matched: mappingResult.matched.length,
      missing_columns: mappingResult.missing.join(', ')
    })
  }

  renderStats (stats) {
    this.statsTarget.innerHTML =
      `<span class="text-green-700">${stats.valid} valid</span> · ` +
      `<span class="text-red-700">${stats.invalid} invalid</span> · ` +
      `${stats.total} total`
  }

  renderTable () {
    const totalPages = Math.max(1, Math.ceil(this.transformedData.length / this.pageSizeValue))
    const pageData = this.transformedData.slice(
      this.currentPage * this.pageSizeValue,
      (this.currentPage + 1) * this.pageSizeValue
    )

    this.tableHeadTarget.innerHTML = this.columnNames
      .map((column) => `<th class="px-2 py-1 text-left border-b bg-gray-50 whitespace-nowrap">${column}</th>`)
      .join('')

    this.tableBodyTarget.innerHTML = pageData.map((row, index) => {
      const rowIndex = typeof row.__rowIndex === 'number' ? row.__rowIndex : index
      const errors = this.rowErrors.get(rowIndex) || []
      const rowParseError = errors.find((error) => error.column === null)
      const rowClass = row.__hasError ? 'bg-red-50' : ''

      const cells = this.columnNames.map((column) => {
        const value = row[column] ?? ''
        const cellError = errors.find((error) => error.column === column)
        const title = cellError ? cellError.message : (rowParseError ? rowParseError.message : '')
        const cellClass = (cellError || rowParseError) ? 'border-red-400 bg-red-100' : 'border-gray-200'

        return `<td class="border p-0 ${cellClass}" title="${this.escapeHtml(title)}">` +
          `<input type="text" class="w-full min-w-[8rem] px-2 py-1 text-xs border-0 bg-transparent" ` +
          `data-action="input->${this.identifier}#updateCell" data-row-index="${index}" ` +
          `data-column="${column}" value="${this.escapeHtml(String(value))}" />` +
          '</td>'
      }).join('')

      return `<tr class="${rowClass}">${cells}</tr>`
    }).join('')

    this.paginationTarget.textContent =
      `Page ${this.currentPage + 1} / ${totalPages}`
  }

  setFileName (name) {
    if (!this.hasFileNameTarget) return
    this.fileNameTarget.textContent = name
    this.fileNameTarget.classList.toggle('hidden', !name)
  }

  showError (message) {
    this.errorBannerTarget.textContent = message
    this.errorBannerTarget.className =
      'rounded-md bg-red-50 p-3 text-sm text-red-800'
    this.errorBannerTarget.classList.remove('hidden')
  }

  showWarning (message) {
    this.errorBannerTarget.textContent = message
    this.errorBannerTarget.className =
      'rounded-md bg-amber-50 p-3 text-sm text-amber-900'
    this.errorBannerTarget.classList.remove('hidden')
  }

  hideError () {
    this.errorBannerTarget.classList.add('hidden')
    this.errorBannerTarget.textContent = ''
  }

  toggleLaunchButton (visible) {
    if (!this.hasLaunchButtonTarget) return
    this.launchButtonTarget.classList.toggle('hidden', !visible)
  }

  toggleRevalidateButton (visible) {
    if (!this.hasRevalidateButtonTarget) return
    this.revalidateButtonTarget.classList.toggle('hidden', !visible)
  }

  escapeHtml (value) {
    return value
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
  }
}
